import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { 
  CreditCard, 
  ShoppingBag, 
  Crown, 
  Search, 
  DollarSign,
  TrendingUp,
  Users,
  Calendar,
  Zap,
  BadgeCheck,
  Wallet
} from 'lucide-react';
import ExportButtons from './ExportButtons';

const PaymentHistory = () => {
  const [activeTab, setActiveTab] = useState('listing'); // 'shop', 'membership', 'stripe', 'paypal', or 'listing'
  const [shopOrders, setShopOrders] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [stripePayments, setStripePayments] = useState([]);
  const [paypalPayments, setPaypalPayments] = useState([]);
  const [listingCredits, setListingCredits] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [customerTypeFilter, setCustomerTypeFilter] = useState('all'); // 'all', 'user', 'trainer'

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  const [syncStatus, setSyncStatus] = useState(null); // 'syncing', 'success', 'error'
  const [lastSyncResult, setLastSyncResult] = useState(null);

  // Auto-sync on page load
  useEffect(() => {
    const initializeData = async () => {
      await syncAllPayments(true); // Silent sync on load
      await fetchData();
    };
    initializeData();
  }, []);

  // Sync all payments and fix order statuses
  const syncAllPayments = async (silent = false) => {
    try {
      if (!silent) setSyncStatus('syncing');
      const token = localStorage.getItem('token');
      
      // Step 1: Sync Stripe payments with orders
      const stripeResponse = await fetch(`${API_BASE}/api/admin/payment-history/sync-stripe-orders`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const stripeData = await stripeResponse.json();
      
      // Step 2: Fix any orders with invalid status (ensures Ship/Delivery buttons work)
      const fixResponse = await fetch(`${API_BASE}/api/admin/payment-history/fix-order-statuses`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const fixData = await fixResponse.json();
      
      const combinedResult = {
        success: stripeData.success && fixData.success,
        updated_count: (stripeData.updated_count || 0) + (fixData.fixed_count || 0),
        created_count: stripeData.created_count || 0,
        fixed_count: fixData.fixed_count || 0
      };
      
      if (combinedResult.success) {
        setLastSyncResult(combinedResult);
        if (!silent) {
          setSyncStatus('success');
          setTimeout(() => setSyncStatus(null), 3000);
        }
        return combinedResult;
      } else {
        if (!silent) setSyncStatus('error');
        return null;
      }
    } catch (error) {
      console.error('Sync error:', error);
      if (!silent) setSyncStatus('error');
      return null;
    }
  };

  // Legacy function name for backward compatibility
  const syncStripePayments = syncAllPayments;

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Fetch shop orders
      const shopResponse = await fetch(`${API_BASE}/api/admin/payment-history/shop-orders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (shopResponse.ok) {
        const shopData = await shopResponse.json();
        setShopOrders(shopData.orders || []);
      }

      // Fetch memberships
      const membershipResponse = await fetch(`${API_BASE}/api/admin/payment-history/memberships`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (membershipResponse.ok) {
        const membershipData = await membershipResponse.json();
        setMemberships(membershipData.memberships || []);
      }

      // Fetch Stripe payments
      const stripeResponse = await fetch(`${API_BASE}/api/admin/payment-history/stripe-payments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (stripeResponse.ok) {
        const stripeData = await stripeResponse.json();
        setStripePayments(stripeData.payments || []);
      }

      // Fetch Listing Credits
      const listingResponse = await fetch(`${API_BASE}/api/admin/payment-history/listing-credits`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (listingResponse.ok) {
        const listingData = await listingResponse.json();
        setListingCredits(listingData.credits || []);
      }

      // Fetch PayPal Payments
      const paypalResponse = await fetch(`${API_BASE}/api/admin/payment-history/paypal-payments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (paypalResponse.ok) {
        const paypalData = await paypalResponse.json();
        setPaypalPayments(paypalData.payments || []);
      }

      // Fetch statistics
      const statsResponse = await fetch(`${API_BASE}/api/admin/payment-history/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats(statsData);
      }

    } catch (error) {
      console.error('Error fetching payment history:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending': { color: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300', label: 'Pending' },
      'paid': { color: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300', label: 'Paid' },
      'completed': { color: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300', label: 'Completed' },
      'active': { color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300', label: 'Active' },
      'expired': { color: 'bg-gray-100 text-gray-800', label: 'Expired' },
      'failed': { color: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300', label: 'Failed' },
      'refunded': { color: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300', label: 'Refunded' },
    };

    const config = statusConfig[status?.toLowerCase()] || statusConfig['pending'];
    return <Badge className={`${config.color} text-xs`}>{config.label}</Badge>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  };

  const filteredShopOrders = shopOrders.filter(order => {
    // Search filter
    const matchesSearch = order.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.order_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer_email.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Customer type filter
    const matchesType = customerTypeFilter === 'all' || 
      (customerTypeFilter === 'user' && order.customer_type.toLowerCase() === 'user') ||
      (customerTypeFilter === 'trainer' && order.customer_type.toLowerCase() === 'trainer') ||
      (customerTypeFilter === 'guest' && order.customer_type.toLowerCase() === 'guest');
    
    return matchesSearch && matchesType;
  });

  const filteredMemberships = memberships.filter(membership =>
    membership.trainer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    membership.package_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    membership.trainer_email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredStripePayments = stripePayments.filter(payment =>
    payment.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    payment.customer_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    payment.package_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
    payment.payment_id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredListingCredits = listingCredits.filter(credit =>
    credit.trainer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    credit.trainer_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (credit.package_name && credit.package_name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const filteredPaypalPayments = paypalPayments.filter(payment =>
    payment.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    payment.customer_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (payment.package_type && payment.package_type.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (payment.paypal_payment_id && payment.paypal_payment_id.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <CreditCard className="w-8 h-8 text-green-600" />
            Payment History
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Track all shop orders and membership purchases</p>
        </div>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card className="p-4 bg-gradient-to-br from-green-50 to-green-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total Revenue</p>
                <p className="text-2xl font-bold text-green-700">
                  ${stats.total_revenue.toFixed(2)}
                </p>
              </div>
              <DollarSign className="w-10 h-10 text-green-600 opacity-50" />
            </div>
          </Card>

          <Card className="p-4 bg-gradient-to-br from-emerald-50 to-emerald-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Listing Credits</p>
                <p className="text-2xl font-bold text-emerald-700">{stats.listing_credits?.total || 0}</p>
                <p className="text-xs text-emerald-600">${(stats.listing_credits?.revenue || 0).toFixed(2)} revenue</p>
              </div>
              <BadgeCheck className="w-10 h-10 text-emerald-600 opacity-50" />
            </div>
          </Card>

          <Card className="p-4 bg-gradient-to-br from-blue-50 to-blue-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Shop Orders</p>
                <p className="text-2xl font-bold text-blue-700">{stats.shop_orders.total}</p>
                <p className="text-xs text-blue-600">${stats.shop_orders.revenue.toFixed(2)} revenue</p>
              </div>
              <ShoppingBag className="w-10 h-10 text-blue-600 opacity-50" />
            </div>
          </Card>

          <Card className="p-4 bg-gradient-to-br from-purple-50 to-purple-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Boost Memberships</p>
                <p className="text-2xl font-bold text-purple-700">{stats.memberships.total}</p>
                <p className="text-xs text-purple-600">${stats.memberships.revenue.toFixed(2)} revenue</p>
              </div>
              <Crown className="w-10 h-10 text-purple-600 opacity-50" />
            </div>
          </Card>

          <Card className="p-4 bg-gradient-to-br from-orange-50 to-orange-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Active Memberships</p>
                <p className="text-2xl font-bold text-orange-700">{stats.memberships.active}</p>
                <p className="text-xs text-orange-600">out of {stats.memberships.total} total</p>
              </div>
              <TrendingUp className="w-10 h-10 text-orange-600 opacity-50" />
            </div>
          </Card>

          {stats.stripe_payments && (
            <Card className="p-4 bg-gradient-to-br from-indigo-50 to-indigo-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Stripe Payments</p>
                  <p className="text-2xl font-bold text-indigo-700">{stats.stripe_payments.total}</p>
                  <p className="text-xs text-indigo-600">
                    {stats.stripe_payments.pending} pending, {stats.stripe_payments.paid} paid
                  </p>
                </div>
                <Zap className="w-10 h-10 text-indigo-600 opacity-50" />
              </div>
            </Card>
          )}

          {stats.paypal_payments && (
            <Card className="p-4 bg-gradient-to-br from-blue-50 to-cyan-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">PayPal Payments</p>
                  <p className="text-2xl font-bold text-blue-700">{stats.paypal_payments.total}</p>
                  <p className="text-xs text-blue-600">
                    ${(stats.paypal_payments.revenue || 0).toFixed(2)} revenue
                  </p>
                </div>
                <Wallet className="w-10 h-10 text-blue-600 opacity-50" />
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex border-b overflow-x-auto">
        <button
          onClick={() => setActiveTab('listing')}
          className={`px-6 py-3 font-semibold transition-colors relative whitespace-nowrap ${
            activeTab === 'listing'
              ? 'text-emerald-600 border-b-2 border-emerald-600'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-800'
          }`}
        >
          <div className="flex items-center gap-2">
            <BadgeCheck className="w-5 h-5" />
            Listing Credits
            <Badge className="bg-emerald-100 text-emerald-700">{listingCredits.length}</Badge>
          </div>
        </button>
        <button
          onClick={() => setActiveTab('shop')}
          className={`px-6 py-3 font-semibold transition-colors relative whitespace-nowrap ${
            activeTab === 'shop'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-800'
          }`}
        >
          <div className="flex items-center gap-2">
            <ShoppingBag className="w-5 h-5" />
            Shop Orders
            <Badge className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">{shopOrders.length}</Badge>
          </div>
        </button>
        <button
          onClick={() => setActiveTab('membership')}
          className={`px-6 py-3 font-semibold transition-colors relative whitespace-nowrap ${
            activeTab === 'membership'
              ? 'text-purple-600 border-b-2 border-purple-600'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-800'
          }`}
        >
          <div className="flex items-center gap-2">
            <Crown className="w-5 h-5" />
            Boost Memberships
            <Badge className="bg-purple-100 text-purple-700">{memberships.length}</Badge>
          </div>
        </button>
        <button
          onClick={() => setActiveTab('stripe')}
          className={`px-6 py-3 font-semibold transition-colors relative whitespace-nowrap ${
            activeTab === 'stripe'
              ? 'text-indigo-600 border-b-2 border-indigo-600'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-800'
          }`}
        >
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5" />
            Stripe Payments
            <Badge className="bg-indigo-100 text-indigo-700">{stripePayments.length}</Badge>
          </div>
        </button>
        <button
          onClick={() => setActiveTab('paypal')}
          className={`px-6 py-3 font-semibold transition-colors relative whitespace-nowrap ${
            activeTab === 'paypal'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-800'
          }`}
        >
          <div className="flex items-center gap-2">
            <Wallet className="w-5 h-5" />
            PayPal Payments
            <Badge className="bg-blue-100 text-blue-700">{paypalPayments.length}</Badge>
          </div>
        </button>
      </div>

      {/* Search and Filters */}
      <div className="flex gap-4 items-center flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <Input
            type="text"
            placeholder="Search by name, email, or order number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        
        {/* Customer Type Filter (only show on shop orders tab) */}
        {activeTab === 'shop' && (
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            <select
              value={customerTypeFilter}
              onChange={(e) => setCustomerTypeFilter(e.target.value)}
              className="px-4 py-2 border border dark:border-gray-700-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-800"
            >
              <option value="all">All Customers</option>
              <option value="user">Users Only</option>
              <option value="trainer">Trainers Only</option>
              <option value="guest">Guests Only</option>
            </select>
          </div>
        )}
        
        <Button onClick={fetchData} variant="outline">
          Refresh
        </Button>
        
        {/* Sync Stripe Payments Button - Always visible */}
        <Button 
          onClick={async () => {
            const result = await syncStripePayments(false);
            if (result && (result.updated_count > 0 || result.created_count > 0)) {
              fetchData(); // Refresh data after sync
            }
          }}
          variant="outline"
          disabled={syncStatus === 'syncing'}
          className={`
            ${syncStatus === 'syncing' ? 'bg-gray-100 text-gray-500' : ''}
            ${syncStatus === 'success' ? 'bg-green-50 hover:bg-green-100 text-green-700 border-green-300' : ''}
            ${syncStatus === 'error' ? 'bg-red-50 hover:bg-red-100 text-red-700 border-red-300' : ''}
            ${!syncStatus ? 'bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border-indigo-300' : ''}
          `}
        >
          <Zap className={`w-4 h-4 mr-2 ${syncStatus === 'syncing' ? 'animate-pulse' : ''}`} />
          {syncStatus === 'syncing' ? 'Syncing...' : 
           syncStatus === 'success' ? `Synced (${lastSyncResult?.updated_count || 0} fixed, ${lastSyncResult?.created_count || 0} created)` : 
           'Sync All Payments'}
        </Button>
      </div>

      {/* Content */}
      {loading ? (
        <Card className="p-12 text-center">
          <p className="text-gray-500 dark:text-gray-400">Loading payment history...</p>
        </Card>
      ) : (
        <>
          {/* Listing Credits Tab */}
          {activeTab === 'listing' && (
            <>
              <ExportButtons 
                data={filteredListingCredits}
                columns={[
                  { header: 'Trainer Name', key: 'trainer_name', accessor: (row) => row.trainer_name || 'N/A' },
                  { header: 'Email', key: 'trainer_email', accessor: (row) => row.trainer_email || 'N/A' },
                  { header: 'Package', key: 'package_name', accessor: (row) => row.package_name || 'N/A' },
                  { header: 'Duration (Days)', key: 'duration_days', accessor: (row) => row.duration_days || 0 },
                  { header: 'Amount ($)', key: 'amount_paid', accessor: (row) => row.amount_paid ? row.amount_paid.toFixed(2) : '0.00' },
                  { header: 'Status', key: 'status', accessor: (row) => row.status || 'N/A' },
                  { header: 'Payment Method', key: 'payment_method', accessor: (row) => row.payment_method || 'N/A' },
                  { header: 'Purchase Date', key: 'created_at', accessor: (row) => formatDate(row.created_at) }
                ]}
                filename="listing-credits-payment-history"
              />
              <Card className="overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700 border-b">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Trainer</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Package</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Duration</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Amount</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Purchase Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredListingCredits.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                          {searchTerm ? 'No listing credits found matching your search' : 'No listing credit purchases yet'}
                        </td>
                      </tr>
                    ) : (
                      filteredListingCredits.map((credit) => (
                        <tr key={credit.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                          <td className="px-6 py-4">
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">{credit.trainer_name}</p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">{credit.trainer_email}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="font-medium text-emerald-700">
                              {credit.package_name || 'Listing Credit'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <BadgeCheck className="w-4 h-4 text-emerald-600" />
                              <span className="font-bold text-emerald-600">
                                {credit.duration_days || 0} days
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="font-bold text-green-600">
                              ${(credit.amount_paid || 0).toFixed(2)}
                            </span>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{credit.currency || 'CAD'}</p>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStatusBadge(credit.status)}
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{credit.payment_method}</p>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                            {formatDate(credit.created_at)}
                          </td>
                        </tr>
                      ))
                    )}
                    </tbody>
                  </table>
                </div>
              </Card>
            </>
          )}

          {/* Shop Orders Tab */}
          {activeTab === 'shop' && (
            <>
              <ExportButtons 
                data={filteredShopOrders}
                columns={[
                  { header: 'Order Number', key: 'order_number', accessor: (row) => row.order_number || 'N/A' },
                  { header: 'Customer Name', key: 'customer_name', accessor: (row) => row.customer_name || 'N/A' },
                  { header: 'Email', key: 'customer_email', accessor: (row) => row.customer_email || 'N/A' },
                  { header: 'Customer Type', key: 'customer_type', accessor: (row) => row.customer_type || 'N/A' },
                  { header: 'Total ($)', key: 'total', accessor: (row) => row.total ? row.total.toFixed(2) : '0.00' },
                  { header: 'Payment Status', key: 'payment_status', accessor: (row) => row.payment_status || 'N/A' },
                  { header: 'Payment Method', key: 'payment_method', accessor: (row) => row.payment_method || 'N/A' },
                  { header: 'Date', key: 'created_at', accessor: (row) => formatDate(row.created_at) }
                ]}
                filename="shop-orders-payment-history"
              />
              <Card className="overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700 border-b">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Order #</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Customer</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          Customer Type
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Items</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Total</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Payment</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredShopOrders.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                          {searchTerm ? 'No orders found matching your search' : 'No shop orders yet'}
                        </td>
                      </tr>
                    ) : (
                      filteredShopOrders.map((order) => (
                        <tr key={order.id} className="hover:bg-gray-50 dark:bg-gray-700">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="font-mono text-sm font-semibold text-blue-600">
                              {order.order_number}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">{order.customer_name}</p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">{order.customer_email}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {order.customer_type.toLowerCase() === 'user' && (
                              <Badge className="bg-blue-100 text-blue-800 text-sm font-semibold px-3 py-1">
                                👤 User
                              </Badge>
                            )}
                            {order.customer_type.toLowerCase() === 'trainer' && (
                              <Badge className="bg-red-100 text-red-800 text-sm font-semibold px-3 py-1">
                                💪 Trainer
                              </Badge>
                            )}
                            {order.customer_type.toLowerCase() === 'guest' && (
                              <Badge className="bg-gray-100 dark:bg-gray-700 text-gray-800 text-sm font-semibold px-3 py-1">
                                🌐 Guest
                              </Badge>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              {order.items.slice(0, 2).map((item, idx) => (
                                <div key={idx}>{item}</div>
                              ))}
                              {order.items.length > 2 && (
                                <div className="text-xs text-gray-400">
                                  +{order.items.length - 2} more
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="font-bold text-green-600">
                              ${order.total.toFixed(2)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStatusBadge(order.payment_status)}
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{order.payment_method}</p>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                            {formatDate(order.created_at)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  </table>
                </div>
              </Card>
            </>
          )}

          {/* Memberships Tab */}
          {activeTab === 'membership' && (
            <>
              <ExportButtons 
                data={filteredMemberships}
                columns={[
                  { header: 'Trainer Name', key: 'trainer_name', accessor: (row) => row.trainer_name || 'N/A' },
                  { header: 'Email', key: 'trainer_email', accessor: (row) => row.trainer_email || 'N/A' },
                  { header: 'Package', key: 'package_name', accessor: (row) => row.package_name || 'N/A' },
                  { header: 'Type', key: 'membership_type', accessor: (row) => row.membership_type || 'N/A' },
                  { header: 'Price ($)', key: 'price_paid', accessor: (row) => row.price_paid ? row.price_paid.toFixed(2) : '0.00' },
                  { header: 'Status', key: 'status', accessor: (row) => row.status || 'N/A' },
                  { header: 'Payment Method', key: 'payment_method', accessor: (row) => row.payment_method || 'N/A' },
                  { header: 'Start Date', key: 'start_date', accessor: (row) => formatDate(row.start_date) },
                  { header: 'End Date', key: 'end_date', accessor: (row) => formatDate(row.end_date) },
                  { header: 'Purchase Date', key: 'created_at', accessor: (row) => formatDate(row.created_at) }
                ]}
                filename="memberships-payment-history"
              />
              <Card className="overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700 border-b">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Trainer</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Package</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Price</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Period</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Purchase Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredMemberships.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                          {searchTerm ? 'No memberships found matching your search' : 'No membership purchases yet'}
                        </td>
                      </tr>
                    ) : (
                      filteredMemberships.map((membership) => (
                        <tr key={membership.id} className="hover:bg-gray-50 dark:bg-gray-700">
                          <td className="px-6 py-4">
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">{membership.trainer_name}</p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">{membership.trainer_email}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="font-medium text-red-700">
                              {membership.package_name}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Badge className="bg-purple-100 text-red-700">
                              {membership.membership_type}
                            </Badge>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="font-bold text-green-600">
                              ${membership.price_paid.toFixed(2)}
                            </span>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{membership.currency}</p>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStatusBadge(membership.status)}
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{membership.payment_method}</p>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              <div>
                                <div>{formatDate(membership.start_date)}</div>
                                <div className="text-xs text-gray-400">to {formatDate(membership.end_date)}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                            {formatDate(membership.created_at)}
                          </td>
                        </tr>
                      ))
                    )}
                    </tbody>
                  </table>
                </div>
              </Card>
            </>
          )}

          {/* Stripe Payments Tab */}
          {activeTab === 'stripe' && (
            <>
              <ExportButtons 
                data={filteredStripePayments}
                columns={[
                  { header: 'Payment ID', key: 'payment_id', accessor: (row) => row.payment_id || 'N/A' },
                  { header: 'Customer Name', key: 'customer_name', accessor: (row) => row.customer_name || 'N/A' },
                  { header: 'Email', key: 'customer_email', accessor: (row) => row.customer_email || 'N/A' },
                  { header: 'Package Type', key: 'package_type', accessor: (row) => row.package_type || 'N/A' },
                  { header: 'Package Name', key: 'package_name', accessor: (row) => row.package_name || 'N/A' },
                  { header: 'Amount ($)', key: 'amount', accessor: (row) => row.amount ? row.amount.toFixed(2) : '0.00' },
                  { header: 'Status', key: 'status', accessor: (row) => row.status || 'N/A' },
                  { header: 'Date', key: 'created_at', accessor: (row) => formatDate(row.created_at) }
                ]}
                filename="stripe-payments-history"
              />
              <Card className="overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700 border-b">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Payment ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Customer</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Package</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Amount</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredStripePayments.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                          {searchTerm ? 'No Stripe payments found matching your search' : 'No Stripe payments yet'}
                        </td>
                      </tr>
                    ) : (
                      filteredStripePayments.map((payment) => (
                        <tr key={payment.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="font-mono text-xs text-indigo-600">
                              {payment.payment_id.substring(0, 20)}...
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">{payment.customer_name}</p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">{payment.customer_email}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div>
                              <Badge className="bg-indigo-100 text-indigo-700">
                                {payment.package_type}
                              </Badge>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{payment.package_name}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="font-bold text-green-600">
                              ${payment.amount.toFixed(2)}
                            </span>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{payment.currency}</p>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStatusBadge(payment.status)}
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Stripe</p>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                            {formatDate(payment.created_at)}
                          </td>
                        </tr>
                      ))
                    )}
                    </tbody>
                  </table>
                </div>
              </Card>
            </>
          )}

          {/* PayPal Payments Tab */}
          {activeTab === 'paypal' && (
            <>
              <ExportButtons 
                data={filteredPaypalPayments}
                columns={[
                  { header: 'PayPal ID', key: 'paypal_payment_id', accessor: (row) => row.paypal_payment_id || 'N/A' },
                  { header: 'Customer Name', key: 'customer_name', accessor: (row) => row.customer_name || 'N/A' },
                  { header: 'Email', key: 'customer_email', accessor: (row) => row.customer_email || 'N/A' },
                  { header: 'Type', key: 'package_type', accessor: (row) => row.package_type || 'N/A' },
                  { header: 'Description', key: 'package_name', accessor: (row) => row.package_name || 'N/A' },
                  { header: 'Amount ($)', key: 'amount', accessor: (row) => row.amount ? row.amount.toFixed(2) : '0.00' },
                  { header: 'Tax ($)', key: 'tax_amount', accessor: (row) => row.tax_amount ? row.tax_amount.toFixed(2) : '0.00' },
                  { header: 'Total ($)', key: 'total_amount', accessor: (row) => row.total_amount ? row.total_amount.toFixed(2) : '0.00' },
                  { header: 'Status', key: 'status', accessor: (row) => row.status || 'N/A' },
                  { header: 'Date', key: 'created_at', accessor: (row) => formatDate(row.created_at) }
                ]}
                filename="paypal-payments-history"
              />
              <Card className="overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700 border-b">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">PayPal Transaction ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Customer</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Amount</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Tax</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredPaypalPayments.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                          <div className="flex flex-col items-center gap-2">
                            <Wallet className="w-12 h-12 text-gray-300" />
                            <p>{searchTerm ? 'No PayPal payments found matching your search' : 'No PayPal payments yet'}</p>
                            <p className="text-xs">PayPal payments will appear here once customers complete transactions</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredPaypalPayments.map((payment) => (
                        <tr key={payment.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <span className="font-mono text-xs text-blue-600">
                                {payment.paypal_payment_id && payment.paypal_payment_id.length > 25 
                                  ? `${payment.paypal_payment_id.substring(0, 25)}...` 
                                  : payment.paypal_payment_id || 'N/A'}
                              </span>
                              <p className="text-xs text-gray-400 mt-1">{payment.source}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">{payment.customer_name}</p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">{payment.customer_email}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div>
                              <Badge className="bg-blue-100 text-blue-700">
                                {payment.package_type}
                              </Badge>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{payment.package_name}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="font-bold text-green-600">
                              ${(payment.amount || 0).toFixed(2)}
                            </span>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{payment.currency || 'CAD'}</p>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-600">
                              ${(payment.tax_amount || 0).toFixed(2)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStatusBadge(payment.status)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                            {formatDate(payment.created_at)}
                          </td>
                        </tr>
                      ))
                    )}
                    </tbody>
                  </table>
                </div>
              </Card>
            </>
          )}
        </>
      )}

      {/* Summary */}
      <div className="text-sm text-gray-600 dark:text-gray-400 mt-4">
        {activeTab === 'listing' && `Showing ${filteredListingCredits.length} of ${listingCredits.length} listing credit purchases`}
        {activeTab === 'shop' && `Showing ${filteredShopOrders.length} of ${shopOrders.length} shop orders`}
        {activeTab === 'membership' && `Showing ${filteredMemberships.length} of ${memberships.length} membership purchases`}
        {activeTab === 'stripe' && `Showing ${filteredStripePayments.length} of ${stripePayments.length} Stripe payments`}
        {activeTab === 'paypal' && `Showing ${filteredPaypalPayments.length} of ${paypalPayments.length} PayPal payments`}
      </div>
    </div>
  );
};

export default PaymentHistory;
